import React, { useRef, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import LogicContext from "../../Context/LogicContext";
import { toLowercaseKeys } from "../../helpers/toLowerCaseKeys";
import { GetBankingList, SetBankingList } from "../../store/bankingAtom";
import { SetExpenseEdit } from "../../store/expensesAtom";
import { SetManualJournalEditAtom } from "../../store/manualJounalAtom";
import { accountService } from "../../services/accountService";
import { createModal } from "react-modal-promise";
import GeneralModal from "../../helpers/GeneralModal";
import { useForm } from 'react-hook-form';
import BankingScroll from "./BankingScroll";
import { bankingService } from "../../services/bankingService";
import { formatCustomNumber } from "../../helpers/formatCustomNumber";
import { formatCustomDate, formatCustomDateWithoutTime } from "../../helpers/formatCustomDate";
import moment from 'moment';
import TransactionModal from "./TransactionModal";
import { AddTransactionPanel } from "./AddTransactionPanel";
import { CreateTransactionPanel } from "./CreateTransactionPanel";
import { CreateManualJournalPanel } from "./CreateManualJournalPanel";
import { CreateExpensePanel } from "./CreateExpensePanel";
import { CreatePaymentReceivedPanel } from "./CreatePaymentReceivedPanel";
import { CreateCreditNotePanel } from "./CreateCreditNotePanel";
import { CreateOfflineRedeemVoucherPanel } from "./CreateOfflineRedeemVoucherPanel";
import { CreateDebitNotePanel } from "./CreateDebitPanel";
import { CreateTransferJournalPanel } from "./CreateTransferJournalPanel";
import { CreateReceiveFromAccountPanel } from "./CreateReceiveFromAccountPanel";


// ─── Icons ────────────────────────────────────────────────────────────────────
const PrintIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
  </svg>
);
const ChevronDown = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);
const SearchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0" />
  </svg>
);
const XIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);
const EditIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);
const TrashIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);
const ReloadIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);



// ─── function_names that have their own dedicated panels ─────────────────────
const HANDLED_PANEL_FUNCTIONS = new Set([
  'expenses', 'manual_journals', 'payment_received',
  'redeem_offline_voucher', 'credit_notes', 'customer_debit_notes',
  'transfer_to_another_account',
]);

// ─── Static transaction types (not fetched from API) ─────────────────────────
const TRANSACTION_TYPES = [
  { transaction_type_id: 1, transaction_name: "Manual Journal", type: "in", menu_name: "Manual Journals", function_name: "manual_journals", module_id: 4, module_name: "Accounts", sql_ref_no: 128, sql_option: 1 },
  { transaction_type_id: 2, transaction_name: "Expense", type: "out", menu_name: "Expenses", function_name: "expenses", module_id: 4, module_name: "Accounts", sql_ref_no: 136, sql_option: 1 },
  { transaction_type_id: 3, transaction_name: "Payment Recieved", display_name: "Payment Receive", type: "in", menu_name: "Payment Received", function_name: "payment_received", module_id: 4, module_name: "Accounts", sql_ref_no: 146, sql_option: 1 },
  { transaction_type_id: 9, transaction_name: "Voucher Redeem", type: "out", menu_name: "Redeem Offline Voucher", function_name: "redeem_offline_voucher", module_id: 2, module_name: "Subscription", sql_ref_no: 95, sql_option: 1 },
  { transaction_type_id: 10, transaction_name: "Credit Notes", type: "out", menu_name: "Credit Notes", function_name: "credit_notes", module_id: 3, module_name: "Inventory", sql_ref_no: 124, sql_option: 1 },
  { transaction_type_id: 18, transaction_name: "Debit Notes", type: "in", menu_name: "Customer Debit Notes", function_name: "customer_debit_notes", module_id: 4, module_name: "Accounts", sql_ref_no: 71, sql_option: 1 },
  { transaction_type_id: 19, transaction_name: "Transfer To Another Account", type: "out", menu_name: "Transfer To Another Account", function_name: "transfer_to_another_account", module_id: 4, module_name: "Accounts", sql_ref_no: 71, sql_option: 1 },
  { transaction_type_id: 20, transaction_name: "Receive From Another Account", type: "in", menu_name: "Receive From Another Account", function_name: "receive_from_another_account", module_id: 4, module_name: "Accounts", sql_ref_no: 71, sql_option: 1 },
];

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const BankingDetailPage = () => {
  const [selectedTransactionType, setSelectedTransactionType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Three panel states: 'none' | 'typePicker' | 'createForm' | 'viewDetail'
  const [activePanel, setActivePanel] = useState('none');
  const [selectedType, setSelectedType] = useState(null);       // for create form
  const [selectedTransaction, setSelectedTransaction] = useState(null); // for view detail
  const [transactionTypeFunction, setTransactionTypeFunction] = useState('');
  const [receiveFromEdit, setReceiveFromEdit] = useState(null);
  const [transferJournalEdit, setTransferJournalEdit] = useState(null);


  const { register, getValues } = useForm();
  const getBankingData = GetBankingList();
  const setBankingData = SetBankingList();
  const setExpenseEdit = SetExpenseEdit();
  const setManualJournalEdit = SetManualJournalEditAtom();
  const myPromiseModal = createModal(GeneralModal);

  const { id } = useParams();
  const navigate = useNavigate();
  const logicContext = useContext(LogicContext);
  const [bankingDetails, setBankingDetails] = useState({ account_transaction: [], account_balance: [] });

  const { pathname } = useLocation();
  const printRef = useRef(null);
  const url = pathname.split("/");
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3];

  useEffect(() => {
    document.title = "Banking Details";
    let isMounted = true;
    if (isMounted && id) loadDetailsData();
    return () => { isMounted = false; };
  }, [id]);

  const loadDetailsData = () => {
    bankingService.bankingDetails(id).then((response) => {
      const serverData = toLowercaseKeys(response.data);
      if (serverData) setBankingDetails(serverData);
    }).catch(err => {
      console.error("Failed to load banking details", err);
      logicContext.showToast("Failed to load details", "error");
    });
  };

  useEffect(() => {
    if (!getBankingData?.bank_info?.length) getBankingListFromAPI();
  }, [getBankingData]);

  const getBankingListFromAPI = useCallback(async () => {
    try {
      const response = await bankingService.bankingList();
      setBankingData(response.data);
    } catch (err) { console.error(err); }
  }, []);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Banking_Statement_${id}`,
  });

  const onSelection = (list) => {/* BankingScroll callback */ };

  const viewItemDetails = useCallback((adId) => {
    navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${adId}`);
  }, [currModuleId, currModuleName, currUserMenu, navigate]);

  const searchByDate = async () => {
    const formData = getValues();
    try {
      const response = await bankingService.bankListByDate({ ...formData, account_id: id });
      const serverData = toLowercaseKeys(response.data);
      if (serverData?.account_transaction) {
        setBankingDetails(prev => ({ ...prev, account_transaction: serverData.account_transaction }));
      }
    } catch {
      setBankingDetails(prev => ({ ...prev, account_transaction: [] }));
    }
  };

  const filteredTransactions = (() => {
    if (!bankingDetails.account_transaction) return [];
    let tx = bankingDetails.account_transaction;
    if (selectedTransactionType !== "all")
      tx = tx.filter(t => t.transaction_type_id === Number(selectedTransactionType));
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      tx = tx.filter(t =>
        (t.account_ref_no?.toString().toLowerCase() || "").includes(q) ||
        (t.journal_number?.toString().toLowerCase() || "").includes(q) ||
        (t.transation_type_name?.toLowerCase() || "").includes(q) ||
        (t.deposit_amount?.toString() || "").includes(q) ||
        (t.withdraw_amount?.toString() || "").includes(q) ||
        (t.running_balance?.toString() || "").includes(q) ||
        (formatCustomDateWithoutTime(t.journal_date)?.toLowerCase() || "").includes(q)
      );
    }
    return tx;
  })();

  const currentBank = getBankingData?.bank_info?.find(b => String(b.account_id) === String(id));
  const bankName = currentBank?.account_name || currentBank?.bank_name || `Account #${id}`;


  const closeAllPanels = () => {
    setActivePanel('none');
    setSelectedType(null);
    setSelectedTransaction(null);
  };

  const onTransactionTypeSelect = (type) => {
    if (type?.function_name === 'expenses') setExpenseEdit({});
    if (type?.function_name === 'manual_journals') setManualJournalEdit([]);
    if (type?.function_name === 'receive_from_another_account') setReceiveFromEdit(null);
    if (type?.function_name === 'transfer_to_another_account') setTransferJournalEdit(null);
    setActivePanel('createForm');
    setSelectedType(type);
    setTransactionTypeFunction(type?.function_name);
  };

  const handleEditExpense = useCallback(async (e, item) => {
    e.stopPropagation();
    try {
      console.log('item ', item)
      const res = await accountService.expenseDetails(item.reference_number);
      const lines = res.data?.expense_info || [];
      if (lines[0]) {
        const attachmentUrls = res.data?.attachment_url || [];
        setExpenseEdit({ ...lines[0], lines, attachmentUrls });
        setSelectedType(TRANSACTION_TYPES.find(t => t.function_name === 'expenses'));
        setTransactionTypeFunction('expenses');
        setActivePanel('createForm');
      } else {
        logicContext.showToast('Expense details not found', 'error');
      }
    } catch {
      logicContext.showToast('Failed to load expense details', 'error');
    }
  }, [setExpenseEdit, logicContext]);

  const handleDeleteExpense = useCallback(async (e, item) => {
    e.stopPropagation();
    const result = await myPromiseModal({
      title: 'Confirm Delete',
      body: `Expense with reference ${item.reference_number} will be permanently deleted.`,
      showInput: false,
      placeHolder: '',
    });
    if (!result?.decision) return;
    try {
      await accountService.deleteExpense(item.reference_number);
      logicContext.showToast('Expense deleted successfully', 'success');
      loadDetailsData();
      closeAllPanels();
    } catch {
      logicContext.showToast('Failed to delete expense', 'error');
    }
  }, [logicContext, myPromiseModal]);

  const handleEditJournal = useCallback(async (e, item) => {
    e.stopPropagation();
    try {
      const res = await accountService.manualJournalDetails(item.account_ref_no);
      if (res.data?.account_info?.length) {
        setManualJournalEdit(res.data.account_info);
        setSelectedType(TRANSACTION_TYPES.find(t => t.function_name === 'manual_journals'));
        setTransactionTypeFunction('manual_journals');
        setActivePanel('createForm');
      } else {
        logicContext.showToast('Journal details not found', 'error');
      }
    } catch {
      logicContext.showToast('Failed to load journal details', 'error');
    }
  }, [setManualJournalEdit, logicContext]);

  const handleDeleteJournal = useCallback(async (e, item) => {
    e.stopPropagation();
    const result = await myPromiseModal({
      title: 'Confirm Delete',
      body: `Journal with reference ${item.account_ref_no} will be permanently deleted.`,
      showInput: false,
      placeHolder: '',
    });
    if (!result?.decision) return;
    try {
      await accountService.deleteManualJournal(item.account_ref_no);
      logicContext.showToast('Journal deleted successfully', 'success');
      loadDetailsData();
      closeAllPanels();
    } catch {
      logicContext.showToast('Failed to delete journal', 'error');
    }
  }, [logicContext, myPromiseModal]);

  const handleEditReceiveFromAccount = useCallback(async (e, item) => {
    e.stopPropagation();
    try {
      const res = await bankingService.bankingTransferDetails(item.account_ref_no);
      const info = res.data?.account_info || [];
      const debitRecord = info.find(r => r.debit_amount > 0) || info[0];
      const creditRecord = info.find(r => r.credit_amount > 0) || info[1];
      if (debitRecord) {
        setReceiveFromEdit({
          account_ref_no: debitRecord.account_ref_no,
          journal_date: debitRecord.journal_date,
          reference_number: debitRecord.reference_number,
          from_account_id: debitRecord.account_id,
          to_account_id: creditRecord?.account_id || '',
          currency_id: debitRecord.currency_id,
          amount: debitRecord.debit_amount,
          notes: debitRecord.notes || '',
          attachment_url: debitRecord.attachement_url || '',
        });
        setSelectedType(TRANSACTION_TYPES.find(t => t.function_name === 'receive_from_another_account'));
        setTransactionTypeFunction('receive_from_another_account');
        setActivePanel('createForm');
      } else {
        logicContext.showToast('Transaction details not found', 'error');
      }
    } catch {
      logicContext.showToast('Failed to load transaction details', 'error');
    }
  }, [logicContext]);

  const handleDeleteReceiveFromAccount = useCallback(async (e, item) => {
    e.stopPropagation();
    const result = await myPromiseModal({
      title: 'Confirm Delete',
      body: `Receive transaction ${item.account_ref_no} will be permanently deleted.`,
      showInput: false,
      placeHolder: '',
    });
    if (!result?.decision) return;
    try {
      await bankingService.deleteReceiveFromAnotherAccount(item.account_ref_no);
      logicContext.showToast('Transaction deleted successfully', 'success');
      loadDetailsData();
      closeAllPanels();
    } catch {
      logicContext.showToast('Failed to delete transaction', 'error');
    }
  }, [logicContext, myPromiseModal, loadDetailsData]);

  const handleEditTransferJournal = useCallback(async (e, item) => {
    e.stopPropagation();
    try {
      const res = await bankingService.bankingTransferDetails(item.account_ref_no);
      const info = res.data?.account_info || [];
      const creditRecord = info.find(r => r.credit_amount > 0) || info[0];
      const debitRecord = info.find(r => r.debit_amount > 0) || info[1];
      if (creditRecord) {
        setTransferJournalEdit({
          account_ref_no: creditRecord.account_ref_no,
          journal_date: creditRecord.journal_date,
          reference_number: creditRecord.reference_number,
          from_account_id: creditRecord.account_id,
          to_account_id: debitRecord?.account_id || '',
          currency_id: creditRecord.currency_id,
          amount: creditRecord.credit_amount,
          notes: creditRecord.notes || '',
          attachment_url: creditRecord.attachement_url || '',
        });
        setSelectedType(TRANSACTION_TYPES.find(t => t.function_name === 'transfer_to_another_account'));
        setTransactionTypeFunction('transfer_to_another_account');
        setActivePanel('createForm');
      } else {
        logicContext.showToast('Transfer details not found', 'error');
      }
    } catch {
      logicContext.showToast('Failed to load transfer details', 'error');
    }
  }, [logicContext]);

  const handleDeleteTransferJournal = useCallback(async (e, item) => {
    e.stopPropagation();
    const result = await myPromiseModal({
      title: 'Confirm Delete',
      body: `Transfer ${item.account_ref_no} will be permanently deleted.`,
      showInput: false,
      placeHolder: '',
    });
    if (!result?.decision) return;
    try {
      await bankingService.deleteReceiveToAnotherAccount(item.account_ref_no);
      logicContext.showToast('Transfer deleted successfully', 'success');
      loadDetailsData();
      closeAllPanels();
    } catch {
      logicContext.showToast('Failed to delete transfer', 'error');
    }
  }, [logicContext, myPromiseModal, loadDetailsData]);

  const getTransactionPanelActions = (item) => {
    switch (item?.transaction_type_id) {
      case 1: return { onEdit: handleEditJournal, onDelete: handleDeleteJournal };
      case 2: return { onEdit: handleEditExpense, onDelete: handleDeleteExpense };
      case 19: return { onEdit: handleEditTransferJournal, onDelete: handleDeleteTransferJournal };
      case 20: return { onEdit: handleEditReceiveFromAccount, onDelete: handleDeleteReceiveFromAccount };
      default: return { onEdit: null, onDelete: null };
    }
  };

  return (
    <section className="mt-4 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

      {/* ── Top Bar ───────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-800">{bankName}</h1>
          <p className="text-xs text-gray-400 mt-0.5">Account ID: {id}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition shadow-sm"
          >
            <PrintIcon />
            <span className="hidden sm:inline">Print</span>
          </button>
          <button
            onClick={loadDetailsData}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition shadow-sm"
            title="Reload transactions"
          >
            <ReloadIcon />
            <span className="hidden sm:inline">Reload</span>
          </button>
          <button
            onClick={() => setActivePanel('typePicker')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition shadow-sm"
          >
            <PlusIcon />
            Add Transaction
            <ChevronDown />
          </button>
        </div>
      </div>

      {/* ── Summary Cards ─────────────────────────────────────────── */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Balance</p>
          <p className="text-xl font-bold text-blue-600">{formatCustomNumber(bankingDetails?.account_balance?.[0]?.balance_amount || 0)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Total Deposits</p>
          <p className="text-xl font-bold text-green-600">{formatCustomNumber(bankingDetails?.account_balance?.[0]?.deposit_amount || 0)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Total Withdrawals</p>
          <p className="text-xl font-bold text-red-600">{formatCustomNumber(bankingDetails?.account_balance?.[0]?.withdraw_amount || 0)}</p>
        </div>


      </div>

      {/* ── Layout ────────────────────────────────────────────────── */}
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-shrink-0">
          <BankingScroll onSelection={onSelection} viewItemDetails={viewItemDetails} />
        </div>

        <div ref={printRef} className="flex-1 bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden">
          <div className="p-6">

            {/* Search & Filter */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
                  <SearchIcon />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search by reference, type, amount…"
                  className="w-full pl-9 pr-9 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-gray-50"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery("")} className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600">
                    <XIcon />
                  </button>
                )}
              </div>
              <select
                value={selectedTransactionType}
                onChange={e => setSelectedTransactionType(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                <option value="all">All Types ({bankingDetails.account_transaction?.length || 0})</option>

                <optgroup label="MONEY OUT">
                  {TRANSACTION_TYPES.filter(t => t.type === 'out').map((type, i) => {
                    const count = bankingDetails.account_transaction?.filter(t => t.transaction_type_id === type.transaction_type_id).length || 0;
                    return <option key={i} value={type.transaction_type_id}>{type.display_name || type.transaction_name} ({count})</option>;
                  })}
                </optgroup>

                <optgroup label="MONEY IN">
                  {TRANSACTION_TYPES.filter(t => t.type === 'in').map((type, i) => {
                    const count = bankingDetails.account_transaction?.filter(t => t.transaction_type_id === type.transaction_type_id).length || 0;
                    return <option key={i} value={type.transaction_type_id}>{type.display_name || type.transaction_name} ({count})</option>;
                  })}
                </optgroup>
              </select>
            </div>

            {/* Date Range */}
            <div className="flex flex-wrap items-end gap-3 mb-5 pb-5 border-b border-gray-100">
              {/* Add relative z-0 (or z-10) to the container wrapping your date inputs */}
              <div className="relative z-0 flex items-center gap-3">

                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-500 whitespace-nowrap">From</span>
                  <input defaultValue={moment(Date.now()).format('YYYY-MM-DD')} {...register('from_date')}
                    className="text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50" type="date" />
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-500 whitespace-nowrap">To</span>
                  <input defaultValue={moment(Date.now()).format('YYYY-MM-DD')} {...register('to_date')}
                    className="text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50" type="date" />
                </div>

              </div>
              <button onClick={searchByDate} type="button" className="text-sm font-semibold text-white px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 transition">
                Search
              </button>
              <span className="ml-auto text-sm text-gray-500 font-medium">
                {filteredTransactions.length} result{filteredTransactions.length !== 1 ? 's' : ''}
                {searchQuery && <span className="text-gray-400 font-normal"> for "{searchQuery}"</span>}
              </span>
            </div>

            {/* Table */}
            <div className="overflow-x-auto rounded-lg border border-gray-100">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <th className="px-4 py-3 text-left">Date</th>
                    <th className="px-4 py-3 text-left">Reference</th>
                    <th className="px-4 py-3 text-left">JOUR. No..</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-right">Deposit</th>
                    <th className="px-4 py-3 text-right">Withdrawal</th>
                    <th className="px-4 py-3 text-right">Balance</th>
                    <th className="px-4 py-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredTransactions.length > 0 ? (
                    filteredTransactions.map((item, idx) => (
                      <tr
                        key={item.item_id || idx}
                        className={`cursor-pointer transition-colors group
                          ${activePanel === 'viewDetail' && selectedTransaction?.item_id === item.item_id
                            ? 'bg-blue-50 border-l-2 border-l-blue-500'
                            : 'hover:bg-blue-50/60'}`}
                        onClick={() => { setSelectedTransaction(item); setActivePanel('viewDetail'); }}
                      >
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatCustomDateWithoutTime(item.journal_date)}</td>
                        <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">{item.account_ref_no || "—"}</td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{item.journal_number || "—"}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="inline-block px-2.5 py-1 text-xs rounded-full font-semibold bg-gray-50 text-gray-700 border border-gray-100">
                              {item.transation_type_name || "Unknown"}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-green-600 whitespace-nowrap">
                          {item.deposit_amount ? formatCustomNumber(item.deposit_amount) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-red-500 whitespace-nowrap">
                          {item.withdraw_amount ? formatCustomNumber(item.withdraw_amount) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-gray-800 whitespace-nowrap">
                          {formatCustomNumber(item.running_balance)}
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          {item.transaction_type_id === 2 && (
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={(e) => handleEditExpense(e, item)}
                                className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition"
                                title="Edit Expense"
                              >
                                <EditIcon />
                              </button>
                              <button
                                onClick={(e) => handleDeleteExpense(e, item)}
                                className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition"
                                title="Delete Expense"
                              >
                                <TrashIcon />
                              </button>
                            </div>
                          )}
                          {item.transaction_type_id === 1 && (
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={(e) => handleEditJournal(e, item)}
                                className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition"
                                title="Edit Journal"
                              >
                                <EditIcon />
                              </button>
                              <button
                                onClick={(e) => handleDeleteJournal(e, item)}
                                className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition"
                                title="Delete Journal"
                              >
                                <TrashIcon />
                              </button>
                            </div>
                          )}
                          {item.transaction_type_id === 20 && (
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={(e) => handleEditReceiveFromAccount(e, item)}
                                className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition"
                                title="Edit Transaction"
                              >
                                <EditIcon />
                              </button>
                              <button
                                onClick={(e) => handleDeleteReceiveFromAccount(e, item)}
                                className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition"
                                title="Delete Transaction"
                              >
                                <TrashIcon />
                              </button>
                            </div>
                          )}
                          {item.transaction_type_id === 19 && (
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={(e) => handleEditTransferJournal(e, item)}
                                className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition"
                                title="Edit Transfer"
                              >
                                <EditIcon />
                              </button>
                              <button
                                onClick={(e) => handleDeleteTransferJournal(e, item)}
                                className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition"
                                title="Delete Transfer"
                              >
                                <TrashIcon />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="text-center py-16 text-gray-400">
                        <div className="flex flex-col items-center gap-2">
                          <svg className="w-10 h-10 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="text-sm">
                            {searchQuery ? `No results for "${searchQuery}"` : selectedTransactionType !== "all" ? `No transactions for the selected type` : "No transactions found"}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ── Shared invisible backdrop for all panels ───────────────── */}
      {activePanel !== 'none' && (
        <div className="fixed inset-0 z-30" onClick={closeAllPanels} />
      )}

      {/* ── Panel 1: Type Picker ───────────────────────────────────── */}
      <AddTransactionPanel
        isOpen={activePanel === 'typePicker'}
        onClose={closeAllPanels}
        transactionTypes={TRANSACTION_TYPES}
        bankName={bankName}
        onSelect={(type) => { onTransactionTypeSelect(type) }}
      />

      {/* ── Panel 2: Create Transaction Form (fallback for unhandled types) ── */}
      <CreateTransactionPanel
        isOpen={activePanel === 'createForm' && !HANDLED_PANEL_FUNCTIONS.has(transactionTypeFunction)}
        onClose={closeAllPanels}
        onBack={() => setActivePanel('typePicker')}
        selectedType={selectedType}
        bankName={bankName}
        accountId={id}
        onSaved={loadDetailsData}
      />

      {/* ── Panel 2: Create Expense Form ──────────────────────── */}
      <CreateExpensePanel
        isOpen={activePanel === 'createForm' && transactionTypeFunction == 'expenses'}
        onClose={closeAllPanels}
        onBack={() => setActivePanel('typePicker')}
        selectedType={selectedType}
        bankName={bankName}
        accountId={id}
        onSaved={loadDetailsData}
        expenseAccountsList={bankingDetails.expense_chart_of_accounts || []}
      />

      {/* ── Panel 3: Create Manual Journal Form ──────────────────────── */}

      <CreateManualJournalPanel
        isOpen={activePanel == 'createForm' && transactionTypeFunction == 'manual_journals'}
        onClose={closeAllPanels}
        onBack={() => setActivePanel('typePicker')}
        selectedType={selectedType}
        bankName={bankName}
        accountId={id}
        onSaved={loadDetailsData}
      />

      {/* ── Panel 3: Create Manual Journal Form ──────────────────────── */}

      <CreatePaymentReceivedPanel
        isOpen={activePanel == 'createForm' && transactionTypeFunction == 'payment_received'}
        onClose={closeAllPanels}
        onBack={() => setActivePanel('typePicker')}
        selectedType={selectedType}
        bankName={bankName}
        accountId={id}
        onSaved={loadDetailsData}
      />
      <CreateCreditNotePanel
        isOpen={activePanel == 'createForm' && transactionTypeFunction == 'credit_notes'}
        onClose={closeAllPanels}
        onBack={() => setActivePanel('typePicker')}
        selectedType={selectedType}
        bankName={bankName}
        accountId={id}
        onSaved={loadDetailsData}
      />
      <CreateOfflineRedeemVoucherPanel
        isOpen={activePanel == 'createForm' && transactionTypeFunction == 'redeem_offline_voucher'}
        onClose={closeAllPanels}
        onBack={() => setActivePanel('typePicker')}
        selectedType={selectedType}
        bankName={bankName}
        accountId={id}
        onSaved={loadDetailsData}
      />

      <CreateDebitNotePanel
        isOpen={activePanel == 'createForm' && transactionTypeFunction == 'customer_debit_notes'}
        onClose={closeAllPanels}
        onBack={() => setActivePanel('typePicker')}
        selectedType={selectedType}
        bankName={bankName}
        accountId={id}
        onSaved={loadDetailsData}
      />

      <CreateTransferJournalPanel
        isOpen={activePanel == 'createForm' && transactionTypeFunction == 'transfer_to_another_account'}
        onClose={() => { closeAllPanels(); setTransferJournalEdit(null); }}
        onBack={() => setActivePanel('typePicker')}
        selectedType={selectedType}
        bankName={bankName}
        accountId={id}
        onSaved={loadDetailsData}
        editData={transferJournalEdit}
        accountsList={bankingDetails.trans_rec_chart_of_accounts || []}
      />


      <CreateReceiveFromAccountPanel
        isOpen={activePanel == 'createForm' && transactionTypeFunction == 'receive_from_another_account'}
        onClose={() => { closeAllPanels(); setReceiveFromEdit(null); }}
        onBack={() => setActivePanel('typePicker')}
        selectedType={selectedType}
        bankName={bankName}
        accountId={id}
        onSaved={loadDetailsData}
        editData={receiveFromEdit}
        accountsList={bankingDetails.trans_rec_chart_of_accounts || []}
      />

      {/* ── Panel 3: View Transaction Detail ──────────────────────── */}
      <TransactionModal
        isOpen={activePanel === 'viewDetail'}
        onClose={closeAllPanels}
        transaction={selectedTransaction}
        bankName={bankName}
        {...getTransactionPanelActions(selectedTransaction)}
      />

    </section>
  );
};

export default BankingDetailPage;